import React, { RefObject, useCallback, useEffect, useRef } from "react";
import { get } from "lodash";
import { BetTypeListItem, ListProps } from "../types";
import { useOverflowCheck } from "../../../../../hooks";
import { Container, List } from "./styled-components";
import Tab from "../Tab";

const ListComp = ({
  isGroupWagerList,
  betTypesList,
  onSelectBetType,
  selectedBetType,
  activeLeg,
  ...props
}: ListProps) => {
  const { containerRef, isOverflowingLeft, isOverflowingRight } =
    useOverflowCheck({ tolerance: 12 });

  const racesRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    let observable: IntersectionObserver;

    if (activeLeg !== undefined) {
      const currentRaceRef = racesRefs?.current[Number(activeLeg)];

      observable = new IntersectionObserver(
        ([entry]) => {
          if (entry.intersectionRatio !== 1) {
            scrollIntoView(activeLeg || 0);
          }
          observable.disconnect();
        },
        {
          threshold: 1.0,
          root: containerRef.current
        }
      );

      if (currentRaceRef) {
        observable.observe(currentRaceRef);
      }
    }

    return () => (observable ? observable.disconnect() : undefined);
  }, [activeLeg]);

  const scrollIntoView = useCallback(
    (leg: string | number) => {
      const raceLeg = racesRefs.current[Number(leg) || 0];
      const container = containerRef.current;

      if (raceLeg && container) {
        const elementOffsetStart = get(raceLeg, "offsetLeft", 0);
        container.scrollTo({
          left: elementOffsetStart,
          behavior: "smooth"
        });
      }
    },
    [racesRefs, containerRef]
  );

  const onRef = useCallback(
    (ref: RefObject<HTMLLIElement>, index: number) => {
      if (ref?.current) {
        racesRefs.current[index] = ref.current;
      }
    },
    [racesRefs]
  );

  return (
    <Container
      {...props}
      hasFadeLeft={isOverflowingLeft}
      hasFadeRight={isOverflowingRight}
    >
      <List data-qa-label="bet-type-selector-list" ref={containerRef}>
        {betTypesList?.map((items, index) => {
          // render only the first element in the nested array
          // this element is the button that opens the menu with all the options
          const item = isGroupWagerList ? get(items, "0") : items;

          // Legs in subWagerMenu
          const isPickBet = item.legCount > 1 && !isGroupWagerList;

          // simplify the tab text to one word if the list is rendered in the menu
          // and if the list for the menu is not pick bets
          // ex: Exacta - Exacta Key = Exacta - Key
          const text =
            isGroupWagerList || isPickBet
              ? item.name
              : item.name.substring(item.name.indexOf(" ") + 1);

          return (
            <Tab
              key={item.id}
              qaLabel={`bet-type-selector-${item.code.toLowerCase()}`}
              onClick={() =>
                onSelectBetType(
                  item,
                  isGroupWagerList ? (items as BetTypeListItem[]) : []
                )
              }
              text={text}
              isActive={
                isPickBet ? item.id === activeLeg : item.id === selectedBetType
              }
              hasPromo={false}
              onRef={(ref) => onRef(ref, index)}
            />
          );
        })}
      </List>
    </Container>
  );
};

export default ListComp;
